import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import { API_ENDPOINTS, axiosConfig } from '../config/api';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(() => {
    // Initialize user from localStorage if available
    const savedUser = localStorage.getItem('user');
    return savedUser ? JSON.parse(savedUser) : null;
  });
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [hasVerified, setHasVerified] = useState(false);

  // Set up axios defaults
  useEffect(() => {
    if (token) {
      axios.defaults.baseURL = axiosConfig.baseURL;
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    }
  }, [token]);

  // Check if user is authenticated on app load (only once)
  useEffect(() => {
    const checkAuth = async () => {
      if (token && !hasVerified) {
        try {
          const response = await axios.get(API_ENDPOINTS.VERIFY);
          if (response.data.success) {
            setIsAuthenticated(true);
            // The verify endpoint returns the token payload directly
            // Normalize user object: JWT payload has 'user_id', but we need 'id'
            const userData = response.data.data;
            if (userData.user_id && !userData.id) {
              userData.id = userData.user_id;
            }
            setUser(userData);
            localStorage.setItem('user', JSON.stringify(userData));
            setHasVerified(true);
          } else {
            // Clear all data if verification fails
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            localStorage.removeItem('userRole');
            localStorage.removeItem('userEmail');
            setToken(null);
            setUser(null);
            setIsAuthenticated(false);
            setHasVerified(false);
            delete axios.defaults.headers.common['Authorization'];
          }
        } catch (error) {
          console.error('Auth verification failed:', error);
          // Clear all data if verification fails
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          localStorage.removeItem('userRole');
          localStorage.removeItem('userEmail');
          setToken(null);
          setUser(null);
          setIsAuthenticated(false);
          setHasVerified(false);
          delete axios.defaults.headers.common['Authorization'];
        }
      } else if (!token) {
        // No token, clear user data and set as not authenticated
        localStorage.removeItem('user');
        localStorage.removeItem('userRole');
        localStorage.removeItem('userEmail');
        setUser(null);
        setIsAuthenticated(false);
        setHasVerified(false);
      } else if (token && hasVerified) {
        // Token exists and already verified, just set as authenticated
        setIsAuthenticated(true);
      }
      setLoading(false);
    };

    checkAuth();
  }, [token, hasVerified]);

  const login = async (email, password) => {
    try {
      const response = await axios.post(API_ENDPOINTS.LOGIN, {
        email,
        password,
      });

      console.log({response});

      if (response.data.success) {
        const { token: newToken, user: userData } = response.data.data;
        
        // Save token to localStorage
        localStorage.setItem('token', newToken);
        setToken(newToken);
        
        // Save user data to localStorage for persistence
        localStorage.setItem('user', JSON.stringify(userData));
        localStorage.setItem('userRole', userData.role_name);
        localStorage.setItem('userEmail', userData.email);
        
        // Save user data including role and email
        setUser(userData);
        setIsAuthenticated(true);
        setHasVerified(true); // Mark as verified since we just logged in
        
        // Set authorization header for future requests
        axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
        
        return { 
          success: true, 
          message: response.data.message,
          user: userData 
        };
      } else {
        return { 
          success: false, 
          message: response.data.message || 'Login failed' 
        };
      }
    } catch (error) {
      console.error('Login error:', error);
      return {
        success: false,
        message: error.response?.data?.message || error.response?.data?.error || 'Login failed',
      };
    }
  };

  const logout = async () => {
    try {
      await axios.post(API_ENDPOINTS.LOGOUT);
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      // Clear all user data from localStorage
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      localStorage.removeItem('userRole');
      localStorage.removeItem('userEmail');
      
      // Clear state
      setToken(null);
      setUser(null);
      setIsAuthenticated(false);
      setHasVerified(false);
      delete axios.defaults.headers.common['Authorization'];
    }
  };

  // Helper functions for easy access to user data
  const getUserRole = () => user?.role_name || localStorage.getItem('userRole');
  const getUserEmail = () => user?.email || localStorage.getItem('userEmail');
  const getUserName = () => user?.full_name || 'User';
  const isAdmin = () => getUserRole() === 'Admin';
  const isSuperAdmin = () => getUserRole() === 'SuperAdmin';
  const isTeacher = () => getUserRole() === 'Teacher';
  const isParent = () => getUserRole() === 'Parent';

  const value = {
    isAuthenticated,
    user,
    setUser,
    loading,
    login,
    logout,
    // Helper functions
    getUserRole,
    getUserEmail,
    getUserName,
    isAdmin,
    isSuperAdmin,
    isTeacher,
    isParent,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
